// src/modules/PeriodicReporting/config/reportTemplates.js
// Report export templates — field mappings and export structure per format.
//
// To support a new export format (e.g. AMLA RTS v1.0):
//   1. Add a new entry to REPORT_TEMPLATES below
//   2. Define metaFieldMappings, dynRowKeys, excludeFromData, exportStructure
//   3. No changes to fiExport.js or PeriodicReportingService.js are needed
//
// The application data model (report.fields) stays stable across format changes.

/**
 * Internal data categories — more granular than any single regulatory report format.
 * Enables storing data that satisfies multiple future formats without changing the data model.
 */
export const DATA_CATEGORIES = {
  META: 'meta',                         // Report metadata (entity, dates, scope)
  AML_METRICS: 'aml_metrics',           // Core AML statistical metrics (FI A.01, A.02...)
  RISK_INDICATORS: 'risk_indicators',   // Risk level indicators per category
  CONTROLS: 'controls',                 // Control effectiveness data
  BWRA_LINK: 'bwra_link',              // Links to BWRA gross/residual risk assessments
  CUSTOMER_SEGMENTS: 'customer_segments', // Dynamic customer/UBO segment rows
  QUALITATIVE: 'qualitative',           // Free-text qualitative narrative
};

/**
 * Template registry.
 * Each entry defines how the internal data model maps to one export format.
 *
 * Template properties:
 *   id                  — unique identifier (used in export metadata and service calls)
 *   name                — human-readable format name
 *   templateVersion     — version string for this template definition
 *   schemaRef           — target schema URI (for documentation/validation)
 *   exportedCategories  — which DATA_CATEGORIES this format covers
 *   metaFieldMappings   — { exportKey: internalFieldsKey } — maps meta fields to export
 *   dynRowKeys          — dynamic row field keys (e.g. 'DYN-ROWS-A_01_02') to export
 *   excludeFromData     — internal field keys excluded from the main data section
 *   exportStructure     — structural config: topLevelKey, dynRowsIncluded
 */
export const REPORT_TEMPLATES = {
  'fi-4.0': {
    id: 'fi-4.0',
    name: 'Finansinspektionen AML Schema v4.0',
    templateVersion: '4.0',
    schemaRef: 'https://schemas.fi.se/aml/nationell/penningtvatt/20251231/aml_schema.json',
    exportedCategories: [
      DATA_CATEGORIES.META,
      DATA_CATEGORIES.AML_METRICS,
      DATA_CATEGORIES.CUSTOMER_SEGMENTS,
    ],
    // Maps export JSON keys → internal field keys stored in report.fields
    metaFieldMappings: {
      reportingDate: '_meta_reportingDate',
      financialEntityId: '_meta_financialEntityId',
      consolidationScope: '_meta_consolidationScope',
    },
    // Dynamic row table keys included in this export format
    dynRowKeys: ['DYN-ROWS-A_01_02', 'DYN-ROWS-A_07_02', 'DYN-ROWS-A_08_01'],
    // These internal keys are excluded from the main 'data' section (meta + schema refs)
    excludeFromData: [
      '_meta_reportingDate',
      '_meta_financialEntityId',
      '_meta_consolidationScope',
      '_schemaRef',
      '_schemaVersion',
    ],
    exportStructure: {
      topLevelKey: 'data',      // FI format uses "data" as the top-level field container
      dynRowsIncluded: true,    // Dynamic row tables are part of the export
    },
  },

  // Placeholder for future AMLA RTS format — expected H2 2026
  // When published, add the template here. fiExport.js and PeriodicReportingService.js
  // require zero changes — only this config file needs updating.
  //
  // 'amla-rts-1.0': {
  //   id: 'amla-rts-1.0',
  //   name: 'AMLA RTS Periodic Reporting v1.0',
  //   templateVersion: '1.0',
  //   schemaRef: 'https://schemas.amla.europa.eu/periodic-reporting/v1.0',
  //   exportedCategories: [DATA_CATEGORIES.META, DATA_CATEGORIES.AML_METRICS, ...],
  //   metaFieldMappings: { reportingDate: '_meta_reportingDate', ... },
  //   dynRowKeys: [],
  //   excludeFromData: ['_meta_reportingDate', ...],
  //   exportStructure: { topLevelKey: 'fields', dynRowsIncluded: false },
  // },
};

/**
 * Get a template by ID.
 * @param {string} templateId
 * @returns {Object|null}
 */
export function getTemplate(templateId) {
  return REPORT_TEMPLATES[templateId] || null;
}

/**
 * Returns summary info for all registered templates (for UI template selectors).
 * @returns {{ id: string, name: string, templateVersion: string }[]}
 */
export function getAvailableTemplates() {
  return Object.values(REPORT_TEMPLATES).map(({ id, name, templateVersion }) => ({
    id,
    name,
    templateVersion,
  }));
}

/** Default template ID used when none is specified */
export const DEFAULT_TEMPLATE_ID = 'fi-4.0';
